import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, GraduationCap, ChevronsUpDown, Info } from "lucide-react";
import { z } from "zod";

const NO_HTML = /^[^<>]*$/;
const DANGEROUS = /<\s*(script|iframe|object|embed|style|link|meta)\b/i;
const CONTACT_EMAIL = "silversagehomeschoolhubs@gmail.com";

type ApplicationType = "existing_class_application" | "new_class_proposal";

type CatalogClass = {
  id: string;
  slug: string | null;
  title: string;
  status: string;
  ages: string | null;
  category: string | null;
  instructor_name: string | null;
};

const STATUS_LABEL: Record<string, string> = {
  published: "Published",
  coming_soon: "Coming Soon",
  instructor_wanted: "Instructor Wanted",
  draft: "Draft",
  hidden: "Hidden",
  archived: "Archived",
  externally_managed: "Privately Managed",
};

function statusLabel(s: string) {
  return STATUS_LABEL[s] ?? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---- Validation ----
const ContactSchema = z.object({
  full_name: z.string().trim().min(2, "Please enter your full name").max(120).regex(NO_HTML, "HTML is not allowed"),
  email: z.string().trim().toLowerCase().email("Please enter a valid email").max(255),
  phone: z.string().trim().refine((v) => {
    const d = v.replace(/[^\d]/g, "");
    return d.length === 10 || d.length === 11;
  }, "Please enter a valid US phone number"),
});

const NewFormSchema = ContactSchema.extend({
  proposed_class_name: z.string().trim().min(2, "Please add a class name").max(200).regex(NO_HTML, "HTML is not allowed"),
  class_description: z.string().trim().min(20, "Please write at least 20 characters").max(4000)
    .refine((v) => !DANGEROUS.test(v), "Please remove markup from the description"),
  age_range: z.string().trim().min(1, "Please specify an age range").max(120).regex(NO_HTML, "HTML is not allowed"),
  experience: z.string().trim().min(1, "Please share your experience").max(4000)
    .refine((v) => !DANGEROUS.test(v), "Please remove markup from your experience"),
  preferred_days: z.string().trim().min(1).max(200).regex(NO_HTML, "HTML is not allowed"),
  preferred_time: z.string().trim().min(1).max(200).regex(NO_HTML, "HTML is not allowed"),
  preferred_length: z.string().trim().min(1).max(200).regex(NO_HTML, "HTML is not allowed"),
  semester_price: z.number().positive("Price must be greater than $0").max(1000, "Price must be $1,000 or less"),
  ack_initials: z.string().trim().regex(/^[A-Za-z]{2,10}$/, "Initials must be 2–10 letters"),
});

const ExistingFormSchema = ContactSchema.extend({
  target_class_id: z.string().uuid("Please select a class"),
  experience: z.string().trim().min(1, "Please share your experience").max(4000)
    .refine((v) => !DANGEROUS.test(v), "Please remove markup from your experience"),
  why_this_class: z.string().trim().min(10, "Please tell us why you want to teach this class").max(4000)
    .refine((v) => !DANGEROUS.test(v), "Please remove markup"),
  availability: z.string().trim().min(1, "Please describe your availability").max(2000)
    .refine((v) => !DANGEROUS.test(v), "Please remove markup"),
  preferred_days: z.string().trim().min(1, "Please share your preferred teaching days").max(200).regex(NO_HTML, "HTML is not allowed"),
  earliest_start_date: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Please enter a valid date").optional().or(z.literal("")),
  resume_url: z.string().trim().max(500).url("Please enter a valid URL").optional().or(z.literal("")),
  certifications: z.string().trim().max(2000).optional(),
  references_text: z.string().trim().max(4000).optional(),
  ack_initials: z.string().trim().regex(/^[A-Za-z]{2,10}$/, "Initials must be 2–10 letters"),
});

// ---- Initial state ----
const initialForm = {
  // shared
  full_name: "",
  email: "",
  phone: "",
  ack_background_check: false,
  ack_standards_of_conduct: false,
  ack_no_guarantee: false,
  ack_teaching_agreement: false,
  ack_minimum_enrollment: false, // new-class only
  ack_initials: "",

  // new-class
  proposed_class_name: "",
  class_description: "",
  age_range: "",
  experience: "",
  preferred_days: "",
  preferred_time: "",
  preferred_length: "",
  semester_price: "",
  requires_supplies: "no",
  supplies_details: "",
  requires_special_space: "no",
  special_space_details: "",

  // existing-class
  target_class_id: "",
  why_this_class: "",
  availability: "",
  earliest_start_date: "",
  resume_url: "",
  certifications: "",
  references_text: "",
};

const WantToTeach = () => {
  const [searchParams] = useSearchParams();
  const prefilledClass = (searchParams.get("class") ?? "").slice(0, 200);

  const [applicationType, setApplicationType] = useState<ApplicationType | "">("");
  const [form, setForm] = useState({ ...initialForm, proposed_class_name: prefilledClass });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [catalog, setCatalog] = useState<CatalogClass[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [classPickerOpen, setClassPickerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      const { data, error } = await (supabase as any).rpc("list_wanttoteach_catalog");
      if (cancelled) return;
      if (error) {
        console.error(error);
        toast.error("Couldn't load the class catalog. Please refresh.");
        setCatalog([]);
      } else {
        setCatalog(
          (data ?? []).map((c: any) => ({
            id: c.id,
            slug: c.slug ?? null,
            title: c.title,
            status: c.status,
            ages: c.ages ?? null,
            category: c.category ?? null,
            instructor_name: c.instructor_name ?? null,
          })),
        );
      }
      setCatalogLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedClass = useMemo(
    () => catalog.find((c) => c.id === form.target_class_id) ?? null,
    [catalog, form.target_class_id],
  );

  const set = <K extends keyof typeof form>(k: K, v: typeof form[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const submitNewClass = async () => {
    const priceNum = parseFloat(form.semester_price);
    const validation = NewFormSchema.safeParse({
      full_name: form.full_name,
      email: form.email,
      phone: form.phone,
      proposed_class_name: form.proposed_class_name,
      class_description: form.class_description,
      age_range: form.age_range,
      experience: form.experience,
      preferred_days: form.preferred_days,
      preferred_time: form.preferred_time,
      preferred_length: form.preferred_length,
      semester_price: Number.isFinite(priceNum) ? priceNum : NaN,
      ack_initials: form.ack_initials,
    });
    if (!validation.success) {
      toast.error(validation.error.issues[0]?.message ?? "Please review the form.");
      return;
    }
    if (!form.ack_minimum_enrollment) {
      toast.error("Please acknowledge the minimum enrollment policy.");
      return;
    }
    if (form.requires_supplies === "yes" && DANGEROUS.test(form.supplies_details)) {
      toast.error("Please remove markup from supplies details.");
      return;
    }
    if (form.requires_special_space === "yes" && DANGEROUS.test(form.special_space_details)) {
      toast.error("Please remove markup from facility details.");
      return;
    }
    const v = validation.data;
    return {
      proposal_type: "new_class_proposal" as const,
      full_name: v.full_name,
      email: v.email,
      phone: v.phone,
      proposed_class_name: v.proposed_class_name,
      class_description: v.class_description,
      age_range: v.age_range,
      experience: v.experience,
      preferred_days: v.preferred_days,
      preferred_time: v.preferred_time,
      preferred_length: v.preferred_length,
      semester_price_cents: Math.round(v.semester_price * 100),
      requires_supplies: form.requires_supplies === "yes",
      supplies_details: form.supplies_details.trim() || null,
      requires_special_space: form.requires_special_space === "yes",
      special_space_details: form.special_space_details.trim() || null,
      ack_background_check: form.ack_background_check,
      ack_standards_of_conduct: form.ack_standards_of_conduct,
      ack_minimum_enrollment: form.ack_minimum_enrollment,
      ack_no_guarantee: form.ack_no_guarantee,
      ack_initials: v.ack_initials,
    };
  };

  const submitExistingClass = async () => {
    const validation = ExistingFormSchema.safeParse({
      full_name: form.full_name,
      email: form.email,
      phone: form.phone,
      target_class_id: form.target_class_id,
      experience: form.experience,
      why_this_class: form.why_this_class,
      availability: form.availability,
      preferred_days: form.preferred_days,
      earliest_start_date: form.earliest_start_date,
      resume_url: form.resume_url,
      certifications: form.certifications,
      references_text: form.references_text,
      ack_initials: form.ack_initials,
    });
    if (!validation.success) {
      toast.error(validation.error.issues[0]?.message ?? "Please review the form.");
      return;
    }
    const v = validation.data;
    return {
      proposal_type: "existing_class_application" as const,
      full_name: v.full_name,
      email: v.email,
      phone: v.phone,
      target_class_id: v.target_class_id,
      experience: v.experience,
      why_this_class: v.why_this_class,
      availability: v.availability,
      preferred_days: v.preferred_days,
      earliest_start_date: v.earliest_start_date || null,
      resume_url: v.resume_url || null,
      certifications: v.certifications || null,
      references_text: v.references_text || null,
      ack_background_check: form.ack_background_check,
      ack_standards_of_conduct: form.ack_standards_of_conduct,
      ack_no_guarantee: form.ack_no_guarantee,
      ack_initials: v.ack_initials,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicationType) {
      toast.error("Please choose an application type first.");
      return;
    }
    if (
      !form.ack_background_check ||
      !form.ack_standards_of_conduct ||
      !form.ack_no_guarantee ||
      !form.ack_teaching_agreement
    ) {
      toast.error("Please review and check all required acknowledgements.");
      return;
    }

    const body =
      applicationType === "new_class_proposal"
        ? await submitNewClass()
        : await submitExistingClass();
    if (!body) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("submit-instructor-proposal", { body });

      if (error) {
        // functions.invoke surfaces non-2xx responses as an opaque error —
        // read the response body so the applicant sees a real message.
        let message = "";
        try {
          const res = (error as any)?.context;
          if (res && typeof res.json === "function") {
            const payload = await res.clone().json();
            message = payload?.error ?? "";
          }
        } catch {
          /* fall through to generic messaging */
        }
        throw new Error(message || "We couldn't save your application. Please try again.");
      }

      if ((data as any)?.error) throw new Error((data as any).error);

      setSubmitted(true);
      if ((data as any)?.emailSent === false) {
        toast.warning(
          "Your application was received, but the confirmation email could not be sent. Our team still has it.",
        );
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      console.error(err);
      const raw = String(err?.message ?? "");
      const friendly =
        !raw || /non-2xx|failed to fetch|network|edge function/i.test(raw)
          ? "We couldn't submit your application right now. Please check your connection and try again."
          : raw;
      toast.error(friendly);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="container mx-auto px-4 py-24 max-w-2xl">
        <Card>
          <CardContent className="p-10 text-center space-y-4">
            <CheckCircle2 className="h-14 w-14 mx-auto text-primary" />
            <h1 className="font-heading text-3xl font-bold">Thank you!</h1>
            <p className="text-muted-foreground">
              Your application has been submitted. Our team will review it within
              5–7 business days and reach out at <strong>{form.email}</strong> if it's a
              fit for the community.
            </p>
            <p className="text-sm text-muted-foreground">
              A confirmation email is on its way — please check your spam folder if
              you don't see it shortly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16 md:py-24 max-w-3xl">
      <div className="text-center mb-10">
        <GraduationCap className="h-10 w-10 mx-auto text-primary mb-3" />
        <h1 className="font-heading text-4xl md:text-5xl font-bold mb-3">Want to Teach?</h1>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto">
          Silver Sage Homeschool Hub is always looking for passionate instructors to enrich
          our community. Apply to teach an existing class or propose a brand-new one.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <Card>
          <CardHeader><CardTitle>About You</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Full name *</Label>
                <Input id="full_name" required maxLength={120}
                  value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" required maxLength={255}
                  value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
            </div>
            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input id="phone" required maxLength={40}
                value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Your Class</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label className="text-base">What would you like to do? *</Label>
              <RadioGroup
                value={applicationType}
                onValueChange={(v) => setApplicationType(v as ApplicationType)}
                className="mt-2 space-y-2"
              >
                <label htmlFor="type_existing" className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/40">
                  <RadioGroupItem value="existing_class_application" id="type_existing" className="mt-0.5" />
                  <div>
                    <div className="font-medium">Apply to teach an existing Silver Sage class</div>
                    <div className="text-sm text-muted-foreground">Choose a class from the current catalog.</div>
                  </div>
                </label>
                <label htmlFor="type_new" className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/40">
                  <RadioGroupItem value="new_class_proposal" id="type_new" className="mt-0.5" />
                  <div>
                    <div className="font-medium">Propose a brand-new class</div>
                    <div className="text-sm text-muted-foreground">Share your original class idea with us.</div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {applicationType === "existing_class_application" && (
              <ExistingClassFields
                catalog={catalog}
                catalogLoading={catalogLoading}
                selectedClass={selectedClass}
                classPickerOpen={classPickerOpen}
                setClassPickerOpen={setClassPickerOpen}
                form={form}
                set={set}
              />
            )}

            {applicationType === "new_class_proposal" && (
              <NewClassFields form={form} set={set} />
            )}
          </CardContent>
        </Card>

        {applicationType === "new_class_proposal" && (
          <Card>
            <CardHeader><CardTitle>Scheduling Preferences</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="preferred_days">Preferred days *</Label>
                <Input id="preferred_days" required maxLength={200}
                  placeholder="e.g. Tuesdays & Thursdays"
                  value={form.preferred_days}
                  onChange={(e) => set("preferred_days", e.target.value)} />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="preferred_time">Preferred time *</Label>
                  <Input id="preferred_time" required maxLength={200}
                    placeholder="e.g. 10 AM – 12 PM"
                    value={form.preferred_time}
                    onChange={(e) => set("preferred_time", e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="preferred_length">Preferred class length *</Label>
                  <Input id="preferred_length" required maxLength={200}
                    placeholder="e.g. 1 hour weekly, 8 weeks"
                    value={form.preferred_length}
                    onChange={(e) => set("preferred_length", e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {applicationType === "new_class_proposal" && (
          <Card>
            <CardHeader><CardTitle>Requirements</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Will you need supplies from the Hub? *</Label>
                <RadioGroup value={form.requires_supplies}
                  onValueChange={(v) => set("requires_supplies", v)}
                  className="flex gap-6 mt-2">
                  <div className="flex items-center gap-2"><RadioGroupItem value="no" id="sup_no" /><Label htmlFor="sup_no">No</Label></div>
                  <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="sup_yes" /><Label htmlFor="sup_yes">Yes</Label></div>
                </RadioGroup>
                {form.requires_supplies === "yes" && (
                  <Textarea className="mt-3" rows={3} maxLength={2000}
                    placeholder="Please describe the supplies you'll need."
                    value={form.supplies_details}
                    onChange={(e) => set("supplies_details", e.target.value)} />
                )}
              </div>
              <div>
                <Label>Does your class require a special space or facility? *</Label>
                <RadioGroup value={form.requires_special_space}
                  onValueChange={(v) => set("requires_special_space", v)}
                  className="flex gap-6 mt-2">
                  <div className="flex items-center gap-2"><RadioGroupItem value="no" id="sp_no" /><Label htmlFor="sp_no">No</Label></div>
                  <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="sp_yes" /><Label htmlFor="sp_yes">Yes</Label></div>
                </RadioGroup>
                {form.requires_special_space === "yes" && (
                  <Textarea className="mt-3" rows={3} maxLength={2000}
                    placeholder="Please describe the space or facility needs."
                    value={form.special_space_details}
                    onChange={(e) => set("special_space_details", e.target.value)} />
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle>Acknowledgements</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {[
              { key: "ack_background_check", label: "I understand that all approved instructors must complete a background check before teaching." },
              { key: "ack_standards_of_conduct", label: "I agree to abide by Silver Sage Homeschool Hub's standards of conduct and community values." },
              ...(applicationType === "new_class_proposal"
                ? [{ key: "ack_minimum_enrollment", label: "I understand that classes are subject to a minimum enrollment of five paid students and may be canceled if not met." }]
                : []),
              { key: "ack_no_guarantee", label: "I understand that submitting this application does not guarantee approval or a teaching position." },
              {
                key: "ack_teaching_agreement",
                label:
                  "Teaching Agreement — I understand that, if approved, I will review and sign Silver Sage Homeschool Hub's annual Teaching Agreement, which covers standards of conduct, class expectations, and payment terms, before I begin teaching.",
              },
            ].map((item) => (
              <div key={item.key} className="flex items-start gap-3">
                <Checkbox
                  id={item.key}
                  checked={(form as any)[item.key]}
                  onCheckedChange={(v) => set(item.key as any, Boolean(v))}
                />
                <Label htmlFor={item.key} className="font-normal leading-snug">{item.label}</Label>
              </div>
            ))}

            <div className="rounded-md border border-muted-foreground/20 bg-muted/40 p-3 text-sm text-muted-foreground">
              <strong className="text-foreground">Tax document notice:</strong> Approved instructors
              may be required to complete a Form W-9 and provide accurate taxpayer information before
              receiving payment. Please <em>do not</em> submit Social Security numbers, EINs, taxpayer
              identification numbers, or completed W-9 information through this public form — we will
              collect that information securely and separately if and when your class is approved.
            </div>

            <div>
              <Label htmlFor="ack_initials">Your initials *</Label>
              <p className="text-xs text-muted-foreground mb-1">
                By entering your initials, you confirm all of the acknowledgements above, including
                the Teaching Agreement acknowledgement.
              </p>
              <Input id="ack_initials" required maxLength={10} className="max-w-[120px]"
                value={form.ack_initials}
                onChange={(e) => set("ack_initials", e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Button type="submit" size="lg" className="w-full" disabled={loading || !applicationType}>
          {loading
            ? "Submitting..."
            : applicationType === "existing_class_application"
              ? "Submit Instructor Application"
              : applicationType === "new_class_proposal"
                ? "Submit Class Proposal"
                : "Choose an application type above"}
        </Button>
        <p className="text-xs text-center text-muted-foreground">
          Questions? Email <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>
        </p>
      </form>
    </div>
  );
};

// ---- Existing-class fields ----
function ExistingClassFields({
  catalog, catalogLoading, selectedClass, classPickerOpen, setClassPickerOpen, form, set,
}: {
  catalog: CatalogClass[];
  catalogLoading: boolean;
  selectedClass: CatalogClass | null;
  classPickerOpen: boolean;
  setClassPickerOpen: (v: boolean) => void;
  form: typeof initialForm;
  set: <K extends keyof typeof initialForm>(k: K, v: typeof initialForm[K]) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Select the class you'd like to teach *</Label>
        <Popover open={classPickerOpen} onOpenChange={setClassPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              className="w-full justify-between mt-1 h-auto py-2 text-left"
              disabled={catalogLoading}
            >
              {selectedClass ? (
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{selectedClass.title}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {statusLabel(selectedClass.status)}
                    {selectedClass.ages ? ` • Ages ${selectedClass.ages}` : ""}
                    {selectedClass.category ? ` • ${selectedClass.category}` : ""}
                    {selectedClass.instructor_name ? ` • ${selectedClass.instructor_name}` : " • No instructor assigned"}
                  </div>
                </div>
              ) : (
                <span className="text-muted-foreground">
                  {catalogLoading ? "Loading class catalog…" : "Search and choose a class…"}
                </span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-[95vw]" align="start">
            <Command>
              <CommandInput placeholder="Search by class name…" />
              <CommandList>
                <CommandEmpty>No classes found.</CommandEmpty>
                <CommandGroup>
                  {catalog.map((c) => (
                    <CommandItem
                      key={c.id}
                      value={`${c.title} ${c.status} ${c.category ?? ""} ${c.instructor_name ?? ""}`}
                      onSelect={() => {
                        set("target_class_id", c.id);
                        setClassPickerOpen(false);
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{c.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {statusLabel(c.status)}
                          {c.ages ? ` • Ages ${c.ages}` : ""}
                          {c.category ? ` • ${c.category}` : ""}
                          {c.instructor_name ? ` • ${c.instructor_name}` : " • No instructor assigned"}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {selectedClass?.instructor_name && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm flex gap-2">
          <Info className="h-4 w-4 mt-0.5 text-primary shrink-0" />
          <span>
            This class already has an instructor assigned. We welcome additional instructor
            interest for substitute teaching, future semesters, or expanded class offerings.
          </span>
        </div>
      )}

      <div>
        <Label htmlFor="why_this_class">Why do you want to teach this class? *</Label>
        <Textarea id="why_this_class" rows={4} maxLength={4000}
          placeholder="Tell us what draws you to this class and what you'd bring to it."
          value={form.why_this_class}
          onChange={(e) => set("why_this_class", e.target.value)} />
      </div>

      <div>
        <Label htmlFor="experience">Relevant experience & qualifications *</Label>
        <Textarea id="experience" rows={4} maxLength={4000}
          placeholder="Teaching background, credentials, or relevant experience."
          value={form.experience}
          onChange={(e) => set("experience", e.target.value)} />
      </div>

      <div>
        <Label htmlFor="availability">Availability *</Label>
        <Textarea id="availability" rows={3} maxLength={2000}
          placeholder="e.g. Weekdays after 3pm, weekend mornings, or specific dates."
          value={form.availability}
          onChange={(e) => set("availability", e.target.value)} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="preferred_days">Preferred teaching days *</Label>
          <Input id="preferred_days" maxLength={200}
            placeholder="e.g. Tuesdays & Thursdays"
            value={form.preferred_days}
            onChange={(e) => set("preferred_days", e.target.value)} />
        </div>
        <div>
          <Label htmlFor="earliest_start_date">Earliest start date</Label>
          <Input id="earliest_start_date" type="date"
            value={form.earliest_start_date}
            onChange={(e) => set("earliest_start_date", e.target.value)} />
        </div>
      </div>

      <div>
        <Label htmlFor="resume_url">Resume link <span className="text-muted-foreground text-xs">(optional)</span></Label>
        <Input id="resume_url" maxLength={500}
          placeholder="https://…  (Google Drive, LinkedIn, personal site)"
          value={form.resume_url}
          onChange={(e) => set("resume_url", e.target.value)} />
      </div>

      <div>
        <Label htmlFor="certifications">Certifications <span className="text-muted-foreground text-xs">(optional)</span></Label>
        <Textarea id="certifications" rows={3} maxLength={2000}
          placeholder="Teaching certifications, CPR, subject-matter certifications, etc."
          value={form.certifications}
          onChange={(e) => set("certifications", e.target.value)} />
      </div>

      <div>
        <Label htmlFor="references_text">References <span className="text-muted-foreground text-xs">(optional)</span></Label>
        <Textarea id="references_text" rows={3} maxLength={4000}
          placeholder="Name, relationship, phone or email — one per line."
          value={form.references_text}
          onChange={(e) => set("references_text", e.target.value)} />
      </div>
    </div>
  );
}

// ---- New-class fields ----
function NewClassFields({
  form, set,
}: {
  form: typeof initialForm;
  set: <K extends keyof typeof initialForm>(k: K, v: typeof initialForm[K]) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="proposed_class_name">Proposed class name *</Label>
        <Input id="proposed_class_name" required maxLength={200}
          value={form.proposed_class_name}
          onChange={(e) => set("proposed_class_name", e.target.value)} />
      </div>
      <div>
        <Label htmlFor="class_description">Class description *</Label>
        <Textarea id="class_description" required rows={4} maxLength={4000}
          placeholder="What will students learn? What's your approach?"
          value={form.class_description}
          onChange={(e) => set("class_description", e.target.value)} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="age_range">Ideal age range *</Label>
          <Input id="age_range" required maxLength={120} placeholder="e.g. Ages 6–10"
            value={form.age_range} onChange={(e) => set("age_range", e.target.value)} />
        </div>
        <div>
          <Label htmlFor="semester_price">Price per student, full 12-week semester (USD) *</Label>
          <Input id="semester_price" type="number" min={0} step="0.01" required
            value={form.semester_price}
            onChange={(e) => set("semester_price", e.target.value)} />
        </div>
      </div>
      <div>
        <Label htmlFor="experience">Your experience & qualifications *</Label>
        <Textarea id="experience" required rows={4} maxLength={4000}
          placeholder="Teaching background, credentials, or relevant experience."
          value={form.experience} onChange={(e) => set("experience", e.target.value)} />
      </div>
    </div>
  );
}

export default WantToTeach;
